import { useModuleBEM } from "@b2bportal/core-themes";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { AccordionContent, Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./GridV2FaqAndTerms.module.scss";
import type { GridV2FaqAndTermsTopic } from "./useGridV2FaqAndTermsDialog";
import { TenantIconNameEnum } from "@/types/tenantIcons";

export interface GridV2FaqAndTermsProps {
  /** Below the desktop breakpoint the rows do not expand: the host opens a sheet for the topic. */
  onOpen: (topic: GridV2FaqAndTermsTopic) => void;
  className?: string;
}

/**
 * The FAQ and Terms and conditions rows at the bottom of the Grid V2 modals.
 * Accordion on desktop; on mobile each row opens a sheet stacked over the
 * modal (see useGridV2FaqAndTermsDialog). The trigger is built on the Radix
 * primitives rather than the core AccordionTrigger: that one puts a chevron
 * after the label, the design puts a plus before it.
 */
export const GridV2FaqAndTerms = ({
  onOpen,
  className,
}: GridV2FaqAndTermsProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "GridV2FaqAndTerms");
  const { isDesktopAndUp } = useDeviceType();

  const rows: {
    topic: GridV2FaqAndTermsTopic;
    label: string;
    content: string;
  }[] = [
    {
      topic: "faq",
      label: t("gridV2.faqAndTerms.faq"),
      content: t("gridV2.faqAndTerms.faqContent"),
    },
    {
      topic: "terms",
      label: t("gridV2.faqAndTerms.termsAndConditions"),
      content: t("gridV2.faqAndTerms.termsContent"),
    },
  ];

  if (!isDesktopAndUp) {
    return (
      <div className={clsx(block, className)}>
        {rows.map(({ topic, label }) => (
          <button
            key={topic}
            type="button"
            className={cn("trigger")}
            onClick={() => onOpen(topic)}
          >
            <Icon iconName={TenantIconNameEnum.plusOutline} aria-hidden />
            {label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <AccordionPrimitive.Root type="multiple" className={clsx(block, className)}>
      {rows.map(({ topic, label, content }) => (
        <AccordionPrimitive.Item
          key={topic}
          value={topic}
          className={cn("item")}
        >
          {/* The heading wrapper is what lets screen readers find the rows by heading. */}
          <AccordionPrimitive.Header className={cn("header")}>
            <AccordionPrimitive.Trigger className={cn("trigger")}>
              <Icon
                className={cn("icon", ["plus"])}
                iconName={TenantIconNameEnum.plusOutline}
                aria-hidden
              />
              <Icon
                className={cn("icon", ["minus"])}
                iconName={TenantIconNameEnum.minusOutline}
                aria-hidden
              />
              {label}
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent>
            <p className={cn("text")}>{content}</p>
          </AccordionContent>
        </AccordionPrimitive.Item>
      ))}
    </AccordionPrimitive.Root>
  );
};
